import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Camera } from '@ionic-native/camera';
import { Crop } from '@ionic-native/crop';
import { AndroidPermissions } from '@ionic-native/android-permissions';
import { EnvVariables } from '../../environments/environment';
import * as uuid from 'uuid';
import * as debug from 'debug';
import * as _ from 'lodash';
import {
  LoadingController,
  Platform,
  ModalController,
  ToastController,
} from 'ionic-angular';
import { FileUploadOptions } from '@ionic-native/file-transfer';
import { AuthProvider } from '../auth/auth';
import { MediaCapture } from '@ionic-native/media-capture';
import { File } from '@ionic-native/file';
import { VideoEditor } from '@ionic-native/video-editor';
import { CrudApiProvider } from '../crud-api/crud-api';
import { UserProvider } from '../user/user';
import { Plugins } from '@capacitor/core';
import { ToolsProvider } from '../tools/tools';
const { BarcodeScanner } = Plugins;

declare var FileTransfer: any;
declare var FileUploadOptions: any;
declare var CmKit: any;

@Injectable()
export class ImageSelectorProvider {
  log = debug('app:ImageSelectorProvider');

  cameraOpts = {
    targetWidth: 1200,
    targetHeight: 1200,
    quality: 85,
    encodingType: this.camera.EncodingType.JPEG,
    mediaType: this.camera.MediaType.PICTURE,
    sourceType: this.camera.PictureSourceType.PHOTOLIBRARY,
    correctOrientation: true,
    destinationType: this.camera.DestinationType.FILE_URI,
  };

  defaultCfgs = {
    actionSheetTitle: 'Picture',
  };

  // stores files we uploaded
  localFileCache: any = {};

  constructor(
    public http: HttpClient,
    public env: EnvVariables,
    private camera: Camera,
    private crop: Crop,
    public loadingCtrl: LoadingController,
    public authProvider: AuthProvider,
    private mediaCapture: MediaCapture,
    private file: File,
    private videoEditor: VideoEditor,
    public platform: Platform,
    public crudApi: CrudApiProvider,
    private androidPermissions: AndroidPermissions,
    public modalCtrl: ModalController,
    public toastCtrl: ToastController,
    public user: UserProvider,
    public tools: ToolsProvider
  ) {
    this.log('ImageSelectorProvider Provider');
    window['ImageSelectorProvider'] = this;
  }

  // Specific types so we don't need to remember configs everywhere
  getPhoto(opts?) {
    let cfgs;
    let type;
    if (opts === 'all') {
      type = 'all';
      cfgs = _.merge(
        {
          actionSheetTitle: 'from messaging split',
        },
        type
      );
    } else {
      type = 'photo';
      cfgs = _.merge(
        {
          actionSheetTitle: 'Photo',
        },
        opts
      );
    }

    return this.getMedia(type, {}, cfgs).then(
      d => {
        return d;
      },
      e => {
        if (!window['cordova'] && e === 'cordova_not_available') {
          this.log('faking response image');
          return Promise.resolve(
            'https://d2r0eic16r3uxv.cloudfront.net/1603910806545952.mp4'
          );
        } else {
          return Promise.reject(e);
        }
      }
    );
  }

  getVideo() {
    return this.getMedia(
      'video',
      {},
      {
        actionSheetTitle: 'Video',
        addGiphy: false,
      }
    ).then(
      d => {
        this.log('d in image selector provider', d);
        return d;
      },
      e => {
        if (!window['cordova'] && e === 'cordova_not_available') {
          this.log('faking response video');
          return Promise.resolve(
            'https://d2z5cdom2mbuj5.cloudfront.net/1531344507504264.mp4'
          );
        } else {
          return Promise.reject(e);
        }
      }
    );
  }

  getAvatar() {
    return this.getMedia(
      'photo',
      {
        targetWidth: 500,
        targetHeight: 500,
        quality: 80,
        allowEdit: true,
      },
      {
        actionSheetTitle: 'Photo',
      }
    ).then(
      d => {
        return d;
      },
      e => {
        if (!window['cordova'] && e === 'cordova_not_available') {
          this.log('faking response image');
          return Promise.resolve(
            'https://d2r0eic16r3uxv.cloudfront.net/1603910806545952.mp4'
          );
        } else {
          return Promise.reject(e);
        }
      }
    );
  }

  getVideoPoster(url: string): Promise<any> {
    if (!window['cordova']) {
      return Promise.resolve(
        'https://d2z5cdom2mbuj5.cloudfront.net/1531344509002491.jpg'
      );
    }

    this.log('trying', url);

    const fileName = this.localFileCache[url] || url;

    this.log('video to get thumbnail from', fileName, url);

    return this.videoEditor
      .createThumbnail({
        fileUri: fileName,
        outputFileName: 'output-name.jpg',
        atTime: 0,
      })
      .then(localThumbnail => {
        this.log('success got local image', localThumbnail);
        return this.uploadS3Direct(localThumbnail, 'jpg');
      })
      .catch(error => {
        this.log('error: ', error);
        return Promise.reject(error);
      });
  }

  getMetadata(url: string): Promise<any> {
    return this.crudApi
      .get('/api/files/metadata', {
        location: url,
      })
      .then(res => {
        return res.data;
      })
      .catch(e => {
        // stifle
        return {};
      });
  }

  scanQRCodeFromExistingImage(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      if (window['cordova']) {
        this.tools.logAnalyticsEvent('trackCustom', 'qrcode-scan-started', {
          userId: this.user.get('_id'),
          method: 'existing-photo',
        });

        this.getPhoto({
          skipActionSheet: true,
          existingPhoto: true,
        })
          .then(imageUrl => {
            this.crudApi
              .post('/api/utilities/readQRCode', {
                url: imageUrl,
              })
              .then(res => {
                this.tools.logAnalyticsEvent(
                  'trackCustom',
                  'qrcode-scan-completed',
                  {
                    userId: this.user.get('_id'),
                    method: 'existing-photo',
                  }
                );

                return resolve(res.data);
              })
              .catch(e => {
                this.log('error getting QR code form image', e);

                this.tools.logAnalyticsEvent(
                  'trackCustom',
                  'qrcode-scan-error',
                  {
                    userId: this.user.get('_id'),
                    method: 'existing-photo',
                  }
                );

                return reject(e);
              });
          })
          .catch(e => {
            this.log('error getting QR code form image', e);

            this.tools.logAnalyticsEvent('trackCustom', 'qrcode-scan-error', {
              userId: this.user.get('_id'),
              method: 'existing-photo',
            });

            return reject(e);
          });
      } else {
        this.log('rejecting because on web');

        const toast = this.toastCtrl.create({
          message: 'Feature not available on web',
          position: 'top',
          duration: 2500,
        });
        toast.present();

        return reject('QR code scanner not available on web');
      }
    });
  }

  scanQRCode(): Promise<any> {
    return new Promise(async (resolve, reject) => {
      if (window['cordova']) {
        const scannerOverlay = document.createElement('div');
        const closeScanner = () => {
          document.getElementsByTagName('html')[0].style.visibility = 'visible';
          scannerOverlay.remove();
          BarcodeScanner.showBackground();
          BarcodeScanner.stopScan();
        };

        this.tools.logAnalyticsEvent('trackCustom', 'qrcode-scan-started', {
          userId: this.user.get('_id'),
          method: 'camera',
        });

        try {
          const checkPermission = async () => {
            this.log('BarcodeScane checking permissions');

            const status = await BarcodeScanner.checkPermission({
              force: false,
            });

            this.log('BarcodeScan checking permissions result', status);

            if (status.granted) {
              // user granted permission
              this.log('BarcodeScan access granted');
              return true;
            }

            if (status.denied) {
              // user denied permission
              this.log('BarcodeScan access denied');
              return false;
            }

            if (status.asked) {
              this.log('BarcodeScan access asked');
              // system requested the user for permission during this call
              // only possible when force set to true
            }

            if (status.neverAsked) {
              this.log('BarcodeScan access never asked, prompting');
              // user has not been requested this permission before
              // it is advised to show the user some sort of prompt
              // this way you will not waste your only chance to ask for the permission

              // const c = confirm(
              //   'We need your permission to use your camera in order to scan QR codes.'
              // );
              // if (!c) {
              //   return false;
              // }
            }

            if (status.restricted || status.unknown) {
              // ios only
              this.log('BarcodeScan access declined');
              // probably means the permission has been denied
              return false;
            }

            this.log('BarcodeScan forcing request');

            // user has not denied permission
            // but the user also has not yet granted the permission
            // so request it
            const statusRequest = await BarcodeScanner.checkPermission({
              force: true,
            });

            if (statusRequest.asked) {
              this.log('BarcodeScan forcing request asked');
              // system requested the user for permission during this call
              // only possible when force set to true
            }

            if (statusRequest.granted) {
              this.log('BarcodeScan forcing request granted');
              // the user did grant the permission now
              return true;
            }

            this.log('BarcodeScan path not found, returning false');

            // user did not grant the permission, so he must have declined the request
            return false;
          };

          this.log('BarcodeScan start permission check');

          const permission = await checkPermission();

          this.log('BarcodeScan permission check complete', permission);

          const render = CmKit.utils.template`
          <div class="qr-scanner-overlay" style="visibility: visible">
            <div
              class="qr-scanner-overlay-top"
              style="
                z-index: 9001;
                position: absolute;
                width: 100%;
                padding: 16px;
              "
            >
              <div
                cm-color="Neutral / White"
                class="qr-scanner-overlay-close SafeArea--top"
                style="font-size: 24px"
              >
                <i class="fal fa-times"></i>
              </div>
            </div>
            <div
              class="qr-scanner-overlay-target"
              style="
                top: 50%;
                right: 50%;
                transform: translate(50%, -50%);
                height: 240px;
                width: 240px;
                background-color: transparent;
                position: absolute;
                border-radius: 12px;
                box-shadow: 0 0 0 9999px rgb(1 1 1 / 45%);
                box-sizing: content-box;
                padding: 10px;
                z-index: 9000;
                border: 3px solid white;
              "
            ></div>
          </div>
          `;

          render(scannerOverlay);

          document.querySelector('html').appendChild(scannerOverlay);

          // same async issues occuring with android related to when the overlay is rendered
          // and when we can set the close listener
          setTimeout(() => {
            const closeButton = document.getElementsByClassName(
              'qr-scanner-overlay-close'
            )[0];

            closeButton.addEventListener('click', () => {
              closeScanner();
              return reject('back button registered');
            });

            this.platform.registerBackButtonAction(e => {
              closeScanner();
            }, 99999);
          }, 100);

          BarcodeScanner.hideBackground();
          document.getElementsByTagName('html')[0].style.visibility = 'hidden';

          const result = await BarcodeScanner.startScan(); // start scanning and wait for a result

          this.log('BarcodeScan code response', result); // log the raw scanned content

          if (_.get(result, 'hasContent')) {
            this.log('BarcodeScan result content', result.content); // log the raw scanned content
            closeScanner();

            this.tools.logAnalyticsEvent(
              'trackCustom',
              'qrcode-scan-completed',
              {
                userId: this.user.get('_id'),
                method: 'camera',
              }
            );

            return resolve(result.content);
          } else {
            closeScanner();

            this.tools.logAnalyticsEvent('trackCustom', 'qrcode-scan-error', {
              userId: this.user.get('_id'),
              method: 'camera',
            });

            return reject('error reading QR code');
          }
        } catch (e) {
          closeScanner();
          this.log('error getting QR code:', e);

          this.tools.logAnalyticsEvent('trackCustom', 'qrcode-scan-error', {
            userId: this.user.get('_id'),
            method: 'camera',
          });

          return reject(e);
        }
      } else {
        this.log('rejecting because on web');

        const toast = this.toastCtrl.create({
          message: 'Feature not available on web',
          position: 'top',
          duration: 2500,
        });
        toast.present();

        return reject('QR code scanner not available on web');
      }
    });
  }

  private getMedia(type = 'photo', cameraOpts?, cfgs?): Promise<any> {
    this.log('android?', this.platform.is('android'));

    if (this.platform.is('android')) {
      this.log('in android?');
      this.androidPermissions
        .checkPermission(
          this.androidPermissions.PERMISSION.READ_EXTERNAL_STORAGE
        )
        .then(
          result => this.log('Has permission?', result.hasPermission),
          err =>
            this.androidPermissions.requestPermission(
              this.androidPermissions.PERMISSION.READ_EXTERNAL_STORAGE
            )
        );
      this.androidPermissions.requestPermissions([
        this.androidPermissions.PERMISSION.READ_EXTERNAL_STORAGE,
      ]);
    }

    if (type === 'all') {
      cameraOpts = _.merge(cameraOpts, {
        mediaType: this.camera.MediaType.ALLMEDIA,
      });
    }

    if (type === 'video') {
      cameraOpts = _.merge(cameraOpts, {
        mediaType: this.camera.MediaType.VIDEO,
      });
    }

    if (type === 'photo') {
      cameraOpts = _.merge(cameraOpts, {
        mediaType: this.camera.MediaType.PICTURE,
      });
    }
    if (_.get(cfgs, 'skipActionSheet')) {
      cameraOpts = _.merge(cameraOpts, {
        mediaType:
          type === 'video'
            ? this.camera.MediaType.VIDEO
            : this.camera.MediaType.PICTURE,
      });

      return this.chooseMedia(
        _.merge(
          {
            sourceType: _.get(cfgs, 'existingPhoto')
              ? this.camera.PictureSourceType.PHOTOLIBRARY
              : this.camera.PictureSourceType.CAMERA,
          },
          cameraOpts
        ),
        cfgs
      );
    }

    return this.actionSheetAddMedia(cameraOpts, cfgs);
  }

  private actionSheetAddMedia(cameraOpts: any, cfgs?): Promise<any> {
    cfgs = _.merge(_.cloneDeep(this.defaultCfgs), cfgs);

    return new Promise((resolve, reject) => {
      let buttons = [];

      if (_.get(cfgs, 'viewImageButton')) {
        buttons.push(_.get(cfgs, 'viewImageButton'));
      }

      if (cfgs.actionSheetTitle === 'from messaging split') {
        buttons.push(
          {
            icon: `<svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.125 1C10.2188 1 10.3125 1.09375 10.3438 1.1875L11.0312 3H14.5C14.75 3 15 3.25 15 3.5V12.5C15 12.7812 14.75 13 14.5 13H1.5C1.21875 13 1 12.7812 1 12.5V3.5C1 3.25 1.21875 3 1.5 3H4.9375L5.5625 1.34375C5.625 1.15625 5.8125 1 6.03125 1H10.125ZM10.125 0H6.03125C5.40625 0 4.84375 0.40625 4.625 1L4.25 2H1.5C0.65625 2 0 2.6875 0 3.5V12.5C0 13.3438 0.65625 14 1.5 14H14.5C15.3125 14 16 13.3438 16 12.5V3.5C16 2.6875 15.3125 2 14.5 2H11.75L11.2812 0.8125C11.0938 0.34375 10.6562 0 10.125 0ZM8 11.75C10.0625 11.75 11.75 10.0938 11.75 8C11.75 5.9375 10.0625 4.25 8 4.25C5.90625 4.25 4.25 5.9375 4.25 8C4.25 10.0938 5.90625 11.75 8 11.75ZM8 5.25C9.5 5.25 10.75 6.5 10.75 8C10.75 9.53125 9.5 10.75 8 10.75C6.46875 10.75 5.25 9.53125 5.25 8C5.25 6.5 6.46875 5.25 8 5.25Z" fill="#262626"/>
            </svg>`,
            text: `Take Photo`,
            handler: () => {
              this.log('Take Photo/Video clicked');
              this.chooseMedia(
                _.merge(
                  { sourceType: this.camera.PictureSourceType.CAMERA },
                  { mediaType: 0 }
                ),
                cfgs
              ).then(
                d => {
                  resolve(d);
                },
                e => {
                  this.log('rejecting', e, cameraOpts);
                  reject(e);
                }
              );
            },
          },
          {
            icon: `<i class="fal fa-video-plus"></i>`,
            text: `Take Video`,
            handler: () => {
              this.log('Take Photo/Video clicked');
              this.chooseMedia(
                _.merge(
                  { sourceType: this.camera.PictureSourceType.CAMERA },
                  { mediaType: 1 }
                ),
                cfgs
              ).then(
                d => {
                  resolve(d);
                },
                e => {
                  this.log('rejecting', e, cameraOpts);
                  reject(e);
                }
              );
            },
          },
          {
            icon: `<svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.5 0H3.5C2.65625 0 2 0.6875 2 1.5V2H1.5C0.65625 2 0 2.6875 0 3.5V12.5C0 13.3438 0.65625 14 1.5 14H14.5C15.3125 14 16 13.3438 16 12.5V12H16.5C17.3125 12 18 11.3438 18 10.5V1.5C18 0.6875 17.3125 0 16.5 0ZM15 12.5C15 12.7812 14.75 13 14.5 13H1.5C1.21875 13 1 12.7812 1 12.5V3.5C1 3.25 1.21875 3 1.5 3H2V10.5C2 11.3438 2.65625 12 3.5 12H15V12.5ZM17 10.5C17 10.7812 16.75 11 16.5 11H3.5C3.21875 11 3 10.7812 3 10.5V1.5C3 1.25 3.21875 1 3.5 1H16.5C16.75 1 17 1.25 17 1.5V10.5ZM5.5 5.25C6.4375 5.25 7.25 4.46875 7.25 3.5C7.25 2.5625 6.4375 1.75 5.5 1.75C4.53125 1.75 3.75 2.5625 3.75 3.5C3.75 4.46875 4.53125 5.25 5.5 5.25ZM5.5 2.75C5.90625 2.75 6.25 3.09375 6.25 3.5C6.25 3.9375 5.90625 4.25 5.5 4.25C5.0625 4.25 4.75 3.9375 4.75 3.5C4.75 3.09375 5.0625 2.75 5.5 2.75ZM13 3.5C12.7188 3.1875 12.25 3.1875 11.9688 3.5L9 6.46875L8 5.5C7.71875 5.1875 7.25 5.1875 6.96875 5.5L4.21875 8.25C4.09375 8.34375 4 8.59375 4 8.75V9.625C4 9.84375 4.15625 10 4.375 10H15.625C15.8125 10 16 9.84375 16 9.625V6.75C16 6.5625 15.9062 6.375 15.75 6.25L13 3.5ZM15 9H5V8.875L7.5 6.375L9 7.875L12.5 4.375L15 6.875V9Z" fill="#262626"/>
            </svg>`,
            text: `Choose Existing Photo/Video`,
            handler: () => {
              this.log(
                'Choose Existing Photo/Video clicked',
                cameraOpts.mediaType,
                this.camera.MediaType.VIDEO
              );

              if (
                this.platform.is('android') &&
                cameraOpts.mediaType !== this.camera.MediaType.VIDEO &&
                cfgs.multi
              ) {
                try {
                  const modal = this.modalCtrl.create('ImageSelectorPage');

                  modal.onDidDismiss(urls => {
                    this.log('got urls', urls);
                    if (urls) {
                      if (!window['cordova']) {
                        this.log('faking response image');
                        resolve(
                          'https://d2r0eic16r3uxv.cloudfront.net/1603910806545952.mp4'
                        );
                      } else {
                        this.log('urls', urls);
                        resolve(urls);
                      }
                    }
                  });

                  modal.present();
                } catch (e) {
                  console.log('error in catch', e);
                  reject(e);
                }
              } else {
                this.chooseMedia(
                  _.merge(
                    { sourceType: this.camera.PictureSourceType.PHOTOLIBRARY },
                    cameraOpts
                  ),
                  cfgs
                ).then(
                  d => {
                    resolve(d);
                  },
                  e => {
                    this.log('rejecting', e, cameraOpts);
                    reject(e);
                  }
                );
              }
            },
          }
        );
      } else {
        buttons.push(
          {
            icon: `<svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.125 1C10.2188 1 10.3125 1.09375 10.3438 1.1875L11.0312 3H14.5C14.75 3 15 3.25 15 3.5V12.5C15 12.7812 14.75 13 14.5 13H1.5C1.21875 13 1 12.7812 1 12.5V3.5C1 3.25 1.21875 3 1.5 3H4.9375L5.5625 1.34375C5.625 1.15625 5.8125 1 6.03125 1H10.125ZM10.125 0H6.03125C5.40625 0 4.84375 0.40625 4.625 1L4.25 2H1.5C0.65625 2 0 2.6875 0 3.5V12.5C0 13.3438 0.65625 14 1.5 14H14.5C15.3125 14 16 13.3438 16 12.5V3.5C16 2.6875 15.3125 2 14.5 2H11.75L11.2812 0.8125C11.0938 0.34375 10.6562 0 10.125 0ZM8 11.75C10.0625 11.75 11.75 10.0938 11.75 8C11.75 5.9375 10.0625 4.25 8 4.25C5.90625 4.25 4.25 5.9375 4.25 8C4.25 10.0938 5.90625 11.75 8 11.75ZM8 5.25C9.5 5.25 10.75 6.5 10.75 8C10.75 9.53125 9.5 10.75 8 10.75C6.46875 10.75 5.25 9.53125 5.25 8C5.25 6.5 6.46875 5.25 8 5.25Z" fill="#262626"/>
            </svg>`,
            text: `Take ${cfgs.actionSheetTitle}`,
            handler: () => {
              this.log('Take Photo/Video clicked');
              this.chooseMedia(
                _.merge(
                  { sourceType: this.camera.PictureSourceType.CAMERA },
                  cameraOpts
                ),
                cfgs
              ).then(
                d => {
                  resolve(d);
                },
                e => {
                  this.log('rejecting', e, cameraOpts);
                  reject(e);
                }
              );
            },
          },
          {
            icon: `<svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.5 0H3.5C2.65625 0 2 0.6875 2 1.5V2H1.5C0.65625 2 0 2.6875 0 3.5V12.5C0 13.3438 0.65625 14 1.5 14H14.5C15.3125 14 16 13.3438 16 12.5V12H16.5C17.3125 12 18 11.3438 18 10.5V1.5C18 0.6875 17.3125 0 16.5 0ZM15 12.5C15 12.7812 14.75 13 14.5 13H1.5C1.21875 13 1 12.7812 1 12.5V3.5C1 3.25 1.21875 3 1.5 3H2V10.5C2 11.3438 2.65625 12 3.5 12H15V12.5ZM17 10.5C17 10.7812 16.75 11 16.5 11H3.5C3.21875 11 3 10.7812 3 10.5V1.5C3 1.25 3.21875 1 3.5 1H16.5C16.75 1 17 1.25 17 1.5V10.5ZM5.5 5.25C6.4375 5.25 7.25 4.46875 7.25 3.5C7.25 2.5625 6.4375 1.75 5.5 1.75C4.53125 1.75 3.75 2.5625 3.75 3.5C3.75 4.46875 4.53125 5.25 5.5 5.25ZM5.5 2.75C5.90625 2.75 6.25 3.09375 6.25 3.5C6.25 3.9375 5.90625 4.25 5.5 4.25C5.0625 4.25 4.75 3.9375 4.75 3.5C4.75 3.09375 5.0625 2.75 5.5 2.75ZM13 3.5C12.7188 3.1875 12.25 3.1875 11.9688 3.5L9 6.46875L8 5.5C7.71875 5.1875 7.25 5.1875 6.96875 5.5L4.21875 8.25C4.09375 8.34375 4 8.59375 4 8.75V9.625C4 9.84375 4.15625 10 4.375 10H15.625C15.8125 10 16 9.84375 16 9.625V6.75C16 6.5625 15.9062 6.375 15.75 6.25L13 3.5ZM15 9H5V8.875L7.5 6.375L9 7.875L12.5 4.375L15 6.875V9Z" fill="#262626"/>
            </svg>`,
            text: `Choose Existing ${cfgs.actionSheetTitle}`,
            handler: () => {
              this.log(
                'Choose Existing Photo/Video clicked',
                cameraOpts.mediaType,
                this.camera.MediaType.VIDEO
              );

              if (
                this.platform.is('android') &&
                cameraOpts.mediaType !== this.camera.MediaType.VIDEO &&
                cfgs.multi
              ) {
                try {
                  const modal = this.modalCtrl.create('ImageSelectorPage');

                  modal.onDidDismiss(urls => {
                    this.log('got urls', urls);
                    if (urls) {
                      if (!window['cordova']) {
                        this.log('faking response image');
                        resolve(
                          'https://d2r0eic16r3uxv.cloudfront.net/1603910806545952.mp4'
                        );
                      } else {
                        this.log('urls', urls);
                        resolve(urls);
                      }
                    }
                  });

                  modal.present();
                } catch (e) {
                  console.log('error in catch', e);
                  reject(e);
                }
              } else {
                this.chooseMedia(
                  _.merge(
                    { sourceType: this.camera.PictureSourceType.PHOTOLIBRARY },
                    cameraOpts
                  ),
                  cfgs
                ).then(
                  d => {
                    resolve(d);
                  },
                  e => {
                    this.log('rejecting', e, cameraOpts);
                    reject(e);
                  }
                );
              }
            },
          }
        );
      }

      if (cfgs.addGiphy) {
        buttons.push({
          icon: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4.5" y="4.5" width="15" height="15" rx="3.86364" fill="white"/>
          <path d="M9.01636 14.3455C8.61394 14.3455 8.25758 14.28 7.94727 14.1491C7.63697 14.0182 7.37273 13.8364 7.15455 13.6036C6.94121 13.3661 6.77879 13.0873 6.66727 12.7673C6.55576 12.4424 6.5 12.0909 6.5 11.7127V11.6545C6.5 11.2764 6.56061 10.9248 6.68182 10.6C6.80303 10.2752 6.97515 9.99394 7.19818 9.75636C7.42121 9.51879 7.68545 9.33455 7.99091 9.20364C8.30121 9.06788 8.64303 9 9.01636 9C9.27333 9 9.52061 9.03152 9.75818 9.09455C9.99576 9.15758 10.2091 9.25697 10.3982 9.39273C10.5921 9.52364 10.7521 9.69333 10.8782 9.90182C11.0091 10.1103 11.0939 10.36 11.1327 10.6509H10.5C10.4273 10.2388 10.2576 9.94545 9.99091 9.77091C9.72909 9.59152 9.40909 9.50182 9.03091 9.50182C8.7303 9.50182 8.46364 9.55515 8.23091 9.66182C7.99818 9.76364 7.80182 9.90909 7.64182 10.0982C7.48182 10.2873 7.36061 10.5127 7.27818 10.7745C7.19576 11.0364 7.15455 11.3273 7.15455 11.6473V11.7055C7.15455 12.0255 7.19333 12.3188 7.27091 12.5855C7.35333 12.8473 7.47212 13.0703 7.62727 13.2545C7.78242 13.4388 7.97636 13.5818 8.20909 13.6836C8.44182 13.7855 8.71091 13.8364 9.01636 13.8364C9.29758 13.8364 9.54 13.7927 9.74364 13.7055C9.94727 13.6182 10.1145 13.5018 10.2455 13.3564C10.3812 13.2061 10.4806 13.0315 10.5436 12.8327C10.6115 12.6291 10.6479 12.4109 10.6527 12.1782H9.13273V11.6545H11.2709V12.04C11.2709 12.4182 11.2152 12.7503 11.1036 13.0364C10.997 13.3224 10.8442 13.5624 10.6455 13.7564C10.4467 13.9503 10.2091 14.0982 9.93273 14.2C9.65636 14.297 9.35091 14.3455 9.01636 14.3455Z" fill="#262626"/>
          <path d="M12.3662 9.08H12.9989V14.28H12.3662V9.08Z" fill="#262626"/>
          <path d="M14.3469 9.08H17.3869V9.59636H14.9796V11.4364H16.856V11.9527H14.9796V14.28H14.3469V9.08Z" fill="#262626"/>
          <path d="M9.01636 14.3455C8.61394 14.3455 8.25758 14.28 7.94727 14.1491C7.63697 14.0182 7.37273 13.8364 7.15455 13.6036C6.94121 13.3661 6.77879 13.0873 6.66727 12.7673C6.55576 12.4424 6.5 12.0909 6.5 11.7127V11.6545C6.5 11.2764 6.56061 10.9248 6.68182 10.6C6.80303 10.2752 6.97515 9.99394 7.19818 9.75636C7.42121 9.51879 7.68545 9.33455 7.99091 9.20364C8.30121 9.06788 8.64303 9 9.01636 9C9.27333 9 9.52061 9.03152 9.75818 9.09455C9.99576 9.15758 10.2091 9.25697 10.3982 9.39273C10.5921 9.52364 10.7521 9.69333 10.8782 9.90182C11.0091 10.1103 11.0939 10.36 11.1327 10.6509H10.5C10.4273 10.2388 10.2576 9.94545 9.99091 9.77091C9.72909 9.59152 9.40909 9.50182 9.03091 9.50182C8.7303 9.50182 8.46364 9.55515 8.23091 9.66182C7.99818 9.76364 7.80182 9.90909 7.64182 10.0982C7.48182 10.2873 7.36061 10.5127 7.27818 10.7745C7.19576 11.0364 7.15455 11.3273 7.15455 11.6473V11.7055C7.15455 12.0255 7.19333 12.3188 7.27091 12.5855C7.35333 12.8473 7.47212 13.0703 7.62727 13.2545C7.78242 13.4388 7.97636 13.5818 8.20909 13.6836C8.44182 13.7855 8.71091 13.8364 9.01636 13.8364C9.29758 13.8364 9.54 13.7927 9.74364 13.7055C9.94727 13.6182 10.1145 13.5018 10.2455 13.3564C10.3812 13.2061 10.4806 13.0315 10.5436 12.8327C10.6115 12.6291 10.6479 12.4109 10.6527 12.1782H9.13273V11.6545H11.2709V12.04C11.2709 12.4182 11.2152 12.7503 11.1036 13.0364C10.997 13.3224 10.8442 13.5624 10.6455 13.7564C10.4467 13.9503 10.2091 14.0982 9.93273 14.2C9.65636 14.297 9.35091 14.3455 9.01636 14.3455Z" stroke="#262626" stroke-width="0.2"/>
          <path d="M12.3662 9.08H12.9989V14.28H12.3662V9.08Z" stroke="#262626" stroke-width="0.2"/>
          <path d="M14.3469 9.08H17.3869V9.59636H14.9796V11.4364H16.856V11.9527H14.9796V14.28H14.3469V9.08Z" stroke="#262626" stroke-width="0.2"/>
          <rect x="4.5" y="4.5" width="15" height="15" rx="3.86364" stroke="#262626"/>
          </svg>
          `,
          text: `Add Giphy Gif`,
          handler: () => {
            const modal = this.modalCtrl.create('GiphyPage');
            modal.onDidDismiss(url => {
              this.log('got Giphy', url);
              if (url) {
                resolve(url);
              }
            });
            modal.present();
          },
        });
      }

      CmKit.ActionSheet({
        props: {
          buttons,
        },
      }).promise.then(res => {
        // no option selected
        if (!res) {
          reject();
        }
      });
    });
  }

  chooseMedia(cfgs?, options?): Promise<any> {
    const opts = _.merge(_.cloneDeep(this.cameraOpts), cfgs);
    let loading = this.loadingCtrl.create({});

    if (_.get(options, 'onLoading')) {
      // diff loading handler
      options.onLoading();
      loading = null;
    } else {
      loading.present();
    }

    if (
      cfgs.mediaType === this.camera.MediaType.VIDEO &&
      cfgs.sourceType === this.camera.PictureSourceType.CAMERA
    ) {
      this.log('getVideoFromCameraLocationCropUpload');

      return this.getVideoFromCameraLocationCropUpload(loading);
    } else {
      this.log('getPhotoLocationCropUpload', opts);

      return this.getPhotoLocationCropUpload(opts, loading, options);
    }
  }

  private getVideoFromCameraLocationCropUpload(loading): Promise<any> {
    this.log('getVideoFromCameraLocationCropUpload');

    return Promise.resolve()
      .then(res => {
        return this.mediaCapture.captureVideo({
          duration: 90,
          quality: 1,
        });
      })
      .then(response => {
        let fileUri = _.get(response, '0.fullPath');
        return this.transcodeVideo(fileUri);
      })
      .then(response => {
        this.log('response local URL: ', response);

        return this.uploadS3Direct(response, 'mp4');
      })
      .then(
        d => {
          loading && loading.dismiss();
          return d;
        },
        e => {
          this.log('error', e);
          loading && loading.dismiss();
          return Promise.reject(e);
        }
      )
      .catch(e => {
        this.log('error: ', e);
        return Promise.reject(e);
      });
  }

  private transcodeVideo(url) {
    this.log('will transcode', url);
    if (url[0] === '/' && this.platform.is('android')) {
      url = 'file://' + url;
      console.log('not transcoding android', url);
      return Promise.resolve(url);
    }

    return this.videoEditor.transcodeVideo({
      fileUri: url,
      outputFileName: uuid.v4() + '.mp4',
      outputFileType: this.videoEditor.OutputFileType.MPEG4,
      optimizeForNetworkUse: this.videoEditor.OptimizeForNetworkUse.YES,
      saveToLibrary: false,
    });
  }

  private getPhotoLocationCropUpload(
    opts: any,
    loading,
    cfgs: any = {}
  ): Promise<any> {
    let edit = false;
    this.log('in getPhotoLocationCropUpload', opts, cfgs);

    // For android, we use the crop plugin because Google Photos doesn't
    // listen to allowEdit
    if (
      opts &&
      opts.allowEdit &&
      (this.platform.is('android') ||
        opts.sourceType === this.camera.PictureSourceType.CAMERA)
    ) {
      edit = true;
      opts.allowEdit = false;
    }

    if (
      opts.mediaType === this.camera.MediaType.PICTURE &&
      opts.sourceType === this.camera.PictureSourceType.PHOTOLIBRARY &&
      cfgs.multi
    ) {
      this.log('getPhotoLocationCropUpload() | PHOTO LIBRARY');

      return new Promise((res, rej) => {
        if (!window['cordova']) {
          loading && loading.dismiss();
          return res([
            'https://d2r0eic16r3uxv.cloudfront.net/1603910806545952.mp4',
            'https://d2r0eic16r3uxv.cloudfront.net/1603910806545952.mp4',
          ]);
        }

        setTimeout(() => {
          loading.dismiss();
        }, 150);

        window['imagePicker'].getPictures(
          multiLocalLocations => {
            this.log(
              'getPhotoLocationCropUpload() | IMG PICKER | Images: ',
              multiLocalLocations
            );

            if (_.size(multiLocalLocations) >= 2) {
              edit = false;
            }

            loading = this.loadingCtrl.create({});
            loading.present();

            const promises = _.map(multiLocalLocations, ll => {
              this.log('before transcodeOrResolve', ll);
              return transcodeOrResolve(this, ll, opts).then(l => {
                this.log('before crop and upload', l);
                return cropAndUpload(this, l, opts);
              });
            });

            return Promise.all(promises)
              .then(locations => {
                this.log(
                  'getPhotoLocationCropUpload() | locations after  cropAndUploadPromises',
                  locations
                );
                loading && loading.dismiss();
                return res(locations);
              })
              .catch(e => {
                console.log('error', e);
                return rej(e);
              });
          },
          e => {
            this.log('getImages() | Error: ' + e);
            loading && loading.dismiss();
            return alert('An error occured' + e);
          },
          {
            maximumImagesCount: 10,
            width: 1200,
            height: 1200,
            quality: 85,
            disable_popover: true,
          }
        );
      });
    } else {
      this.log('getPhotoLocationCropUpload() | VIDEO | IMAGE');

      return this.camera
        .getPicture(opts)
        .catch(e => {
          this.log('error getting photo', e);
        })
        .then(localLocation => {
          return transcodeOrResolve(this, localLocation, opts);
        })
        .then(localLocation => {
          return cropAndUpload(this, localLocation, opts);
        })
        .then(
          d => {
            loading && loading.dismiss();
            return d;
          },
          e => {
            this.log('error', e, opts);
            loading && loading.dismiss();
            return Promise.reject(e);
          }
        );
    }

    function transcodeOrResolve(self, localLocation, opts) {
      self.log('in getPicture then', localLocation, opts.MediaType);
      if (opts.mediaType === self.camera.MediaType.VIDEO) {
        return self.transcodeVideo(localLocation);
      } else {
        return Promise.resolve(localLocation);
      }
    }

    function cropAndUpload(self, localLocation, opts) {
      self.log('will upload from localLocation', localLocation, opts);

      const ext =
        opts.mediaType === self.camera.MediaType.VIDEO ? 'mp4' : 'jpg';

      if (edit) {
        self.log('trying to crop');
        return self.crop
          .crop(localLocation, {
            targetWidth: opts.targetWidth,
            targetHeight: opts.targetHeight,
          })
          .then(newLocalImageLocation => {
            self.log('newLocalImageLocation', newLocalImageLocation);
            return self.uploadS3Direct(newLocalImageLocation, ext);
          })
          .catch(err => {
            return alert('An unknown error occured');
          });
      } else {
        self.log('skipping crop');
        return self.uploadS3Direct(localLocation, ext);
      }
    }
  }

  uploadS3Direct(imageUri, extension) {
    this.log('imageURI extension', imageUri, extension);

    let letter1 = _.nth(imageUri, imageUri.length - 3);
    let letter2 = _.nth(imageUri, imageUri.length - 2);
    let letter3 = _.nth(imageUri, imageUri.length - 1);
    let fileType = `${letter1}${letter2}${letter3}`;

    if (fileType === 'MOV') {
      extension = 'mp4';
    }

    return new Promise((resolve, reject) => {
      var uploadData: any = {
        filename: '',
        date: '',
        credential: '',
        policy: '',
        signature: '',
      };
      this.log(`s3 upload route: /api/files/security/${extension}`, extension);
      this.crudApi
        .get(`/api/files/security/${extension}`, {
          cachebust: new Date().getTime() + Math.random(),
        })
        .then(res => {
          uploadData = res.data;
          this.log('security api response', uploadData);

          var imageURI = imageUri;
          var options = new FileUploadOptions();
          options.fileKey = 'file';
          var time = new Date().getTime();
          var fileName = uploadData.filename;
          options.fileName = fileName;

          if (extension === 'jpg') {
            options.mimeType = 'image/' + extension;
          } else if (extension === 'mp4') {
            options.mimeType = 'video/' + extension;
          }
          options.chunkedMode = false;

          var uri = encodeURI(
            'https://cdllifemobile-uploads.s3.amazonaws.com/'
          );

          var params = {
            key: uploadData.filename,
            acl: 'public-read',
            'Content-Type': options.mimeType,
            'x-amz-credential': uploadData.credential,
            'x-amz-date': uploadData.date,
            'x-amz-algorithm': 'AWS4-HMAC-SHA256',
            policy: uploadData.policy,
            'x-amz-signature': uploadData.signature,
          };
          options.params = params;

          this.log('options', options);
          this.log('params', params);

          var ft = new FileTransfer();
          ft.upload(
            imageURI,
            uri,
            (res, e) => {
              const url =
                'https://d2z5cdom2mbuj5.cloudfront.net/' + uploadData.filename;
              this.log('final url', url);
              this.localFileCache[url] = imageUri;
              this.log('local file cache now', this.localFileCache);
              return resolve(url);
            },
            (res, e) => {
              this.log('fail', res, e);
              return reject(e);
            },
            options
          );
        })
        .catch(e => {
          // stifle
          this.log('security api error', e);
          return reject(e);
        }); // hit route;
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/image-selector/image-selector.ts